import { GenericService } from 'services/genericService';
import api from 'utils/api';
import { Observable, of, throwError } from 'rxjs';
import Cookies from 'js-cookie';
import { finalize, switchMap } from 'rxjs/operators';
import { catchError, map, tap } from 'rxjs/operators';
import { dispatch } from 'redux/store';
import { setLoginSuccessful, logOut, lockScreen, setLogin } from 'redux/modules/session/actions';
import { invalidateAllCache } from 'redux/modules/api/cache/actions';
import { Auth } from 'utils/api/models/authorization/auth';
import * as payloads from 'utils/api/payloads/omantelApi';
import moment from 'moment';
import { setTimestamp } from 'redux/modules/timestamp/actions';
import { IResetPassword } from 'utils/api/models/authorization/resetPassword';
import { unLoadQualtrics } from 'utils/qualtrics';
import { resetNewHeaderFooterEnabled } from 'redux/modules/settings/remoteConfig/actions';
import { encryptPassword } from 'utils/crypto/crypto';

export class AuthorizationService extends GenericService {
	public static signInByCredentials(
		username: string,
		password: string,
		isLogin2FAEnabled: boolean,
		isPasswordEncryptionEnabled: boolean
	): Observable<Auth> {
		if (isLogin2FAEnabled || isPasswordEncryptionEnabled) {
			return this.getPasswordEncryptionKey(isPasswordEncryptionEnabled, username).pipe(
				switchMap((encryptionKey) => this.getEncryptedPassword(password, encryptionKey)),
				switchMap((encryptedPassword: string) =>
					api.securityServices
						.doLoginWithOTP(username, encryptedPassword, isPasswordEncryptionEnabled)
						.call()
						.pipe(
							map((r) => {
								if (r.loginCode === 'LOGIN_SUCCESS') {
									this.setLoggedInSession(username);
								}

								return r;
							})
						)
				)
			);
		}

		return api.omantelPortal
			.doLogin(username, password)
			.call()
			.pipe(tap(() => this.setLoggedInSession(username)));
	}

	public static validateLoginOTP(username: string, OTP: string): Observable<Auth> {
		return api.securityServices
			.validateLoginOTP(username, OTP)
			.call()
			.pipe(
				map((r) => {
					this.setLoggedInSession(username);
					return r?.response;
				}),
				catchError((e: any) => {
					return throwError(e);
				})
			);
	}

	private static setLoggedInSession(username: string) {
		const currentTimestamp = moment().format('x');
		dispatch(setTimestamp(currentTimestamp));
		dispatch(setLoginSuccessful());
		dispatch(setLogin({ login: username }));
	}

	public static getPasswordEncryptionKey(isPasswordEncryptionEnabled: boolean, username: string): Observable<any> {
		if (!isPasswordEncryptionEnabled) {
			return new Observable<string>((observer) => {
				observer.next(undefined);
				observer.complete();
			});
		}

		return api.securityServices
			.getPasswordEncryptionKey(username)
			.call()
			.pipe(map(({ response }) => response?.data));
	}

	private static getEncryptedPassword(password: string, encryptionKey: string | null): Observable<string> {
		return encryptionKey ? encryptPassword(password, encryptionKey) : of(password);
	}

	public static authenticate(token: string): Observable<any> {
		return api.omantelPortal.authenticate(token).call();
	}

	public static initializeResetPassword(userInfo: payloads.UserInfo): Observable<IResetPassword> {
		return api.omantelApi.initializeResetPassword(userInfo).call();
	}

	public static requestResetPasswordOtp(login: string): Observable<any> {
		return api.omantelApi
			.requestResetPasswordOtp(login)
			.withErrorHandling()
			.disableCache()
			.call();
	}

	public static validateResetPasswordEmail(email: string, token: string): Observable<any> {
		return api.omantelApi
			.validateResetPasswordEmail(email, token)
			.disableCache()
			.call();
	}

	public static resetPassword(data: payloads.ResetPasswordData): Observable<any> {
		return api.omantelApi
			.resetPassword(data)
			.withErrorHandling()
			.disableCache()
			.call();
	}

	public static changePassword(data: payloads.ChangePasswordData): Observable<any> {
		return api.omantelApi
			.changePassword(data)
			.disableCache()
			.call();
	}

	public static logOut(silent = false): Observable<any> {
		if (!silent) {
			dispatch(resetNewHeaderFooterEnabled());
			dispatch(invalidateAllCache());
			dispatch(logOut());
			unLoadQualtrics();
		}
		return api.omantelPortal
			.doLogout()
			.call()
			.pipe(
				finalize(() => {
					Cookies.remove('correlationId', { domain: '.omantel.om' });
				})
			);
	}

	public static lockScreen(): void {
		dispatch(lockScreen());
	}
}
